import { executeService } from '@/services';
import { buildUrlWithLoanParams } from './urlBuilder';
import type {
  LoanSetupData,
  LoanApplicationData,
} from '@/store/slices/loanApplication.slice';

/**
 * User registration response from the auth service
 */
export interface UserRegistrationResponse {
  access_token?: string;
  refresh_token?: string;
  expires_in?: number;
  token_type?: string;
  session_id?: string;
}

/**
 * User registration payload structure
 */
export interface UserRegistrationPayload {
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  attributes: {
    phone: string[];
    passwordCreated: string[];
  };
}

/**
 * Loan application state structure
 */
interface LoanApplicationState {
  loanSetup: LoanSetupData;
  application: LoanApplicationData;
}

/**
 * Options for registering and redirecting a user
 */
export interface RegisterAndRedirectOptions {
  /** Base URL for the POS app redirect */
  baseUrl: string;
  /** Loan application state from Redux */
  loanApplication: LoanApplicationState;
  /** User's role (default: 'borrower') */
  role?: string;
}

/**
 * User details for registration
 */
export interface UserDetails {
  firstName?: string;
  lastName?: string;
  email?: string;
}

/**
 * Generates a unique email using timestamp and random suffix
 * @returns Object containing unique email and random letters for name suffix
 */
export const generateUniqueUserData = (): {
  uniqueEmail: string;
  randomLetters: string;
} => {
  const timestamp = Date.now();
  const randomLetters = Math.random()
    .toString(36)
    .substring(2, 4)
    .toUpperCase();
  const uniqueEmail = `khanolkar.omi007${timestamp}@gmail.com`;

  return { uniqueEmail, randomLetters };
};

/**
 * Creates a user registration payload with user details or fallback to generated data
 * @param userDetails - Optional user details from form
 * @returns Registration payload object
 */
export const createRegistrationPayload = (
  userDetails?: UserDetails
): UserRegistrationPayload => {
  const { uniqueEmail, randomLetters } = generateUniqueUserData();

  // Use provided user details or fall back to generated data
  const email = userDetails?.email || uniqueEmail;
  const firstName = userDetails?.firstName || `Omkar ${randomLetters}`;
  const lastName = userDetails?.lastName || `Khanolkar ${randomLetters}`;

  return {
    username: email,
    email: email,
    firstName: firstName,
    lastName: lastName,
    attributes: {
      phone: ['8655519950'],
      passwordCreated: ['false'],
    },
  };
};

/**
 * Registers a new user via the registerUser service
 * @param userDetails - Optional user details from form
 * @returns The registration response containing tokens and session info
 */
export const registerUser = async (
  userDetails?: UserDetails
): Promise<UserRegistrationResponse | null> => {
  const registrationPayload = createRegistrationPayload(userDetails);

  const response = await executeService({
    service: 'registerUser',
    args: registrationPayload as unknown as Record<string, unknown>,
  });

  console.log('User Registration Response:', response);

  return response as UserRegistrationResponse | null;
};

/**
 * Registers a new user and redirects to the POS app with auth tokens
 * @param options - Configuration options for registration and redirect
 * @returns The URL that was redirected to, or null if registration failed
 */
export const registerAndRedirect = async (
  options: RegisterAndRedirectOptions
): Promise<string | null> => {
  const { baseUrl, loanApplication, role = 'borrower' } = options;

  // Extract user details from loanApplication
  const userDetails: UserDetails = {
    firstName: loanApplication.application.firstName,
    lastName: loanApplication.application.lastName,
    email: loanApplication.application.email,
  };

  try {
    const registrationResponse = await registerUser(userDetails);

    if (registrationResponse?.access_token) {
      const urlWithParams = buildUrlWithLoanParams({
        baseUrl,
        loanApplication,
        token: registrationResponse.access_token,
        refreshToken: registrationResponse.refresh_token,
        expiresIn: registrationResponse.expires_in,
        tokenType: registrationResponse.token_type,
        sessionId: registrationResponse.session_id,
        role,
      });

      console.log('Navigating to URL:', urlWithParams);
      window.location.href = urlWithParams;
      // window.open(urlWithParams, '_blank');

      return urlWithParams;
    }

    return null;
  } catch (error) {
    console.error('User Registration Error:', error);
    throw error;
  }
};
